import {
  CaffeineBreakdown,
  DietPreference,
  Ethnicity,
  FeedbackType,
  Gender,
  GeneDataGroupArea,
  GeneDataGroupType,
  Intolerance,
  LoginError,
  PasswordResetError,
  RegisterError,
  ResilienceLevel,
  VerifyCodeError,
} from "../api/smartmelts-api/graphql/generated";
import { MenuItemCategory, MenuItemSubcategory } from "../models/types/category-types";
import { UpdatePasswordError } from "./../api/smartmelts-api/graphql/generated";
import { nutrientSourcesEN } from "./en/nutrient-sources";

const nutrientTranslations = {
  B_CAROTENE: {
    name: "ß-carotene",
    info:
      "These vitamins and other important substances are contained in coloured vegetables and can aid certain genetic types in processes like detoxification, protection from free radicals and cognitive health.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Supports gene expression ",
      [GeneDataGroupType.EYE]: "Supports eye health",
      [GeneDataGroupType.BRAIN]: "Protects against mental decline",
      [GeneDataGroupType.JOINT]: "Helps to ease arthritic pain",
      [GeneDataGroupType.METABOLISM]: "Supports the immune system",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Protects against photo oxidative damage",
      [GeneDataGroupType.GLUTEN]: "Supports the immune system",
    },
  },
  FOLIC_ACID: {
    name: "Folic acid",
    info:
      "These vitamins help in regulating the homocysteine levels in blood, which are influenced by certain genetic variants. High homocysteine levels have a negative impact on heart health and should be kept low.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Crucial for proper brain function",
      [GeneDataGroupType.BRAIN]: "Aids in the production of DNA and RNA",
      [GeneDataGroupType.HOMOCYSTEINE]: "Used to produce energy",
    },
  },
  VIT_A: {
    name: "Vitamin A",
    info:
      "These vitamins and other important substances are contained in coloured vegetables and can aid certain genetic types in processes like detoxification, protection from free radicals and cognitive health.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Gene expression and cell differentiation",
    },
  },
  VIT_B2: {
    name: "Vitamin B2",
    info:
      "Similar to folic acid, this vitamin can aid in stabilizing homocysteine levels but only if a certain genetic variant is present. If this is not the case, vitamin B2 has no effect on homocysteine levels.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Converts carbohydrates into glucose",
      [GeneDataGroupType.BRAIN]: "Reduces nerve inflammation",
      [GeneDataGroupType.HOMOCYSTEINE_B2]: "Reduces homocysteine levels",
      [GeneDataGroupType.HYPERTENSION]: "Lowers blood pressure",
      [GeneDataGroupType.METABOLISM]: "Contributes to iron metabolism",
      [GeneDataGroupType.GLUTEN]: "Converts carbohydrates into glucose",
    },
  },
  VIT_B6: {
    name: "Vitamin B6/B12",
    info:
      "These vitamins help in regulating the homocysteine levels in blood, which are influenced by certain genetic variants. High homocysteine levels have a negative impact on heart health and should be kept low.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Maintains normal nerve function",
      [GeneDataGroupType.BRAIN]: "Helps to create neurotransmitters",
      [GeneDataGroupType.HOMOCYSTEINE]: "Lowers homocysteine levels",
      [GeneDataGroupType.METABOLISM]: "Supports carbohydrate, protein and fat metabolism",
      [GeneDataGroupType.GLUTEN]: "Maintains normal nerve function",
    },
  },
  VIT_C: {
    name: "Vitamin C",
    info:
      "These substances are strong antioxidants and can have a positive impact on cognitive health, eye health, joint health and protection from free radicals.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Supports growth and tissue repair",
      [GeneDataGroupType.BONE]: "Essential for collagen formation",
      [GeneDataGroupType.EYE]: "Protects against cataracts",
      [GeneDataGroupType.BRAIN]: "Regulates neurotransmitter synthesis",
      [GeneDataGroupType.METABOLISM]: "Supports protein metabolism",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Reduces free-radical damage",
      [GeneDataGroupType.GLUTEN]: "Supports growth and tissue repair",
    },
  },
  VIT_D3: {
    name: "Vitamin D",
    info:
      "This vitamin can be produced by the skin using sunlight and is an important component for healthy bones. Certain genetic types require higher doses of this vitamin to maintain optimal bone health.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Maintains normal blood calcium and phosphorus levels",
      [GeneDataGroupType.BONE]: "Helps calcium and phsophorus absorbtion",
      [GeneDataGroupType.BRAIN]: "Protects memory function",
      [GeneDataGroupType.METABOLISM]: "Supports protein metabolism",
      [GeneDataGroupType.GLUTEN]: "Supports the immune system",
      [GeneDataGroupType.LACTOSE]: "Maintains normal blood calcium levels",
    },
  },
  VIT_E: {
    name: "Vitamin E",
    info:
      "These substances are strong antioxidants and can have a positive impact on cognitive health, eye health, joint health and protection from free radicals.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Acts as an anitoxidant",
      [GeneDataGroupType.EYE]: "Protects eyes from free-radicals",
      [GeneDataGroupType.BRAIN]: "Maintains brain health",
      [GeneDataGroupType.METABOLISM]: "Acts in cells damaged by free radicals",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Attenuates oxidative damage",
      [GeneDataGroupType.GLUTEN]: "Acts as an anitoxidant",
    },
  },
  IRON: {
    name: "Iron",
    info:
      "Iron is an important component of blood that allows it to carry oxygen in the form of haemoglobin. Some genetic variants can cause excessive absorption of iron which can damage organs.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Helps carry oxygen around the body",
      [GeneDataGroupType.METABOLISM]: "Helps carry oxygen around the body",
      [GeneDataGroupType.DETOX]: "Helps break down and remove toxins",
      [GeneDataGroupType.GLUTEN]: "Helps carry oxygen around the body",
    },
  },
  POTASSIUM: {
    name: "Potassium",
    info:
      "Potassium is an important nutrient for a variety of bodily functions, and has a strong impact on blood pressure and cognitive health. Certain genetic types benefit from a higher potassium intake.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Supports a regular heartbeat",
      [GeneDataGroupType.BRAIN]: "Helps brain cell communication",
    },
  },
  CALCIUM: {
    name: "Calcium",
    info:
      "Calcium is an important component of many bodily functions. Certain genetic types require higher amounts of calcium for optimal bone health and to aid in detoxification.",
    functions: {
      [GeneDataGroupType.TOTAL]:
        "Important for bone and teeth health, involved in clotting, muscle contractions and nerve functions",
      [GeneDataGroupType.BONE]: "Breaks down carbohydrates",
      [GeneDataGroupType.METABOLISM]: "Interacts with a large number of nutrients for cell metabolism",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Stimulates antioxidants",
      [GeneDataGroupType.DETOX]: "Supports body's defence against toxins",
      [GeneDataGroupType.GLUTEN]: "Alternative gluten free sources needed",
      [GeneDataGroupType.LACTOSE]: "Alternative non-dairy sources needed",
    },
  },
  COPPER: {
    name: "Copper",
    info:
      "Copper is an important component of many enzymes and is therefore an important trace element for metabolism. It has also been shown to reduce the aggressiveness of the immune system and to improve eye health.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Helps to maintain healthy bones, blood vessels, nerves, and immune function.",
      [GeneDataGroupType.EYE]: "Enhances antioxidant enzyme functions",
      [GeneDataGroupType.METABOLISM]: "Energy metabolism and detoxification",
      [GeneDataGroupType.GLUTEN]: "Alternative gluten free sources needed",
    },
  },
  MAGNESIUM: {
    name: "Magnesium",
    info:
      "Magnesium is an essential component of more than 300 enzymes, and therefore particularly important for metabolism, muscle cell function and bone health.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Supports muscle and nerve function",
    },
  },
  MANGANESE: {
    name: "Manganese",
    info:
      "Manganese is an important component of many enzymes and has an impact on neutralizing free radicals and protecting the joints. Certain genetic types benefit in these areas if manganese intake is increased.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Aids cognitive tissue, bone, and sex hormone production",
      [GeneDataGroupType.BRAIN]: "Neccesary for brain and nerve function",
      [GeneDataGroupType.METABOLISM]: "Activates enzymes",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Reduces oxidative stress",
      [GeneDataGroupType.GLUTEN]: "Neccesary for brain and nerve function",
    },
  },
  SODIUM: {
    name: "Sodium",
    info: "Sodium is a component of table salt and can lead to an increase in blood pressure in some people.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Supports the body's electrolyte balance",
      [GeneDataGroupType.BONE]: "Excessive intake may lead to osteoporosis",
      [GeneDataGroupType.BRAIN]: "Affects nerve impulse transmission",
      [GeneDataGroupType.HYPERTENSION]: "Affects pH balance and blood pressure ",
      [GeneDataGroupType.METABOLISM]: "Maintains normal cellular homeostasis",
    },
  },
  PHOSPHORUS: {
    name: "Phosphoric acid",
    info: "These substances are contained in many types of food and can have a negative impact on bone health.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Affects the body's energy use and storage",
      [GeneDataGroupType.BONE]: "In excess may lead to bone loss",
    },
  },
  SELENE: {
    name: "Selenium",
    info:
      "Selenium is an important component of many enzymes, some of which can neutralize free radicals. Certain genetic types need higher amounts of selenium to increase antioxidant protection.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Involved in reproduction, hormones and DNA sythesis ",
      [GeneDataGroupType.BRAIN]: "Prevents mental decline and memory loss",
      [GeneDataGroupType.METABOLISM]: "Helps produce thyroid hormones",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Lowers oxidative stress",
      [GeneDataGroupType.DETOX]: "Reduces oxidative stress",
      [GeneDataGroupType.GLUTEN]: "Helps produce thyroid hormones",
    },
  },
  ZINC: {
    name: "Zinc",
    info:
      "Zinc is needed by the body for optimal macronutrient metabolism, proper protein construction, healthy bone maintenance and to create new DNA. It is an essential micronutrient for many proteins and enzymes.It is a strong antioxidants and can have a positive impact on cognitive health, eye health, joint health and protection from free radicals.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Supports the immune system",
      [GeneDataGroupType.EYE]: "Formats the retina's visual pigment",
      [GeneDataGroupType.BRAIN]: "Involved in nerve impulses transmission",
      [GeneDataGroupType.METABOLISM]:
        "Enables the degradation and synthesis of lipids, proteins, carbohydrates and nucleic acids",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Involved in protein metabolism",
      [GeneDataGroupType.DETOX]: "Defends against antioxidants",
      [GeneDataGroupType.GLUTEN]: "Supports the immune system",
    },
  },
  PHYTIC_ACID: {
    name: "Phytic acid",
    info: "These substances are contained in many types of food and can have a negative impact on bone health.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Inhibits micronutrient bioavailability",
      [GeneDataGroupType.BONE]: "Reduces risk of bone fractures",
    },
  },
  FIBER: {
    name: "Fibre",
    info:
      "Fibre consists of indigestible plant material that does not enter the body but remains in the intestine where it aids in mechanical breakdown of food components.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Helps reguate the body's use of sugars",
      [GeneDataGroupType.HEART]: "Helps prevents hearts disease",
      [GeneDataGroupType.BRAIN]: "Helps delay brain ageing",
      [GeneDataGroupType.METABOLISM]: "Keeps hunger and blood sugar in check",
      [GeneDataGroupType.GLUTEN]: "Alternative gluten free sources needed",
    },
  },
  KOFFEINE: {
    name: "Caffeine",
    info: "Caffeine can have a negative impact on bone health and should be avoided by certain genetic types.",
    functions: {
      [GeneDataGroupType.TOTAL]:
        "Stimulates the central nervous system, heart, muscles, and the centres that control blood pressure",
      [GeneDataGroupType.HEART]: "Stimulates the intensity of heart contractions",
      [GeneDataGroupType.BONE]: "Decreases bone mineral density",
      [GeneDataGroupType.OXIDATIVESTRESS]: "May contribute to oxidative stress ",
    },
  },
  LUTEINE: {
    name: "Lutein",
    info:
      "Lutein is a substance from the group of carotenes and have shown to be beneficial for eye health. Therefore, an increased intake of this substance is beneficial for certain genetic types.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Protects eye tissue from sunlight",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Reduces oxidative stress",
      [GeneDataGroupType.DETOX]: "Helps fight free-radical damage",
    },
  },
  LYCOPENE: {
    name: "Lycopene",
    info:
      "Lycopene is a substance from the group of carotenes and have shown to be beneficial for eye health. Therefore, an increased intake of this substance is beneficial for certain genetic types.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Provides free-radical protection",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Provides free-radical protection",
      [GeneDataGroupType.DETOX]: "Can prevent damage from toxins",
    },
  },
  MSM: {
    name: "Methylsulfonylmethane",
    info:
      "This organic sulphur compound reduces the aggressiveness of the immune system and can help protect joints in certain genetic types.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Decreases inflammation ",
      [GeneDataGroupType.JOINT]: "Reduces joint pain",
    },
  },
  OXALIC_ACID: {
    name: "Oxalic acid",
    info: "These substances are contained in many types of food and can have a negative impact on bone health.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Binds with minerals",
      [GeneDataGroupType.BONE]: "Binds with calcium",
    },
  },
  PHYTOSTEROL: {
    name: "Phytosterol",
    info:
      "In genetic types, where omega-3 fatty acids have a negative impact on HDL cholesterol levels, phytosterols are a good alternative to improve cholesterol levels.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Blocks cholesterol absorbtion",
      [GeneDataGroupType.HEART]: "Enhances heart health",
    },
  },
  A_LINOLENIC_ACID: {
    name: "Alpha linolenic acid",
    info:
      "Alpha-linolenic acid is important for the conversion of omega-3 fatty acids and has a positive effect on joint health. However, this substance can also have a negative impact on cholesterol levels in certain genetic types.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Necessary for normal human growth and development",
      [GeneDataGroupType.HEART]: "Improves cardiovascular health",
      [GeneDataGroupType.EYE]: "Promotes eye health",
      [GeneDataGroupType.JOINT]: "Reduces inflammation ",
    },
  },
  OMEGA3: {
    name: "Omega-3 fatty acids",
    info:
      "This general term describes all omega-3 fatty acids (there are several types) which can be found in fish and fish oils, as well as some plants. They have a positive impact on joint health but can have a negative impact on HDL cholesterol in some genetic types.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Regulates blood clotting, artery walls and inflammation",
    },
  },
  DHA: {
    name: "Docosahexaenoic acid (DHA)",
    info:
      "These omega-3 fatty acids are important for regulation of cholesterol levels and correct function of the immune system, joints and brain. Some genetic types do, however, experience a negative effect on the HDL cholesterol levels, which needs to be considered.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Key for eye and nerve tissue development",
      [GeneDataGroupType.HEART]: "Increases good cholesterol",
      [GeneDataGroupType.EYE]: "Improves vision",
      [GeneDataGroupType.BRAIN]: "Improves brain function",
      [GeneDataGroupType.JOINT]: "Promotes reduction of inflammation",
      [GeneDataGroupType.METABOLISM]: "Cell signalling and communication",
      [GeneDataGroupType.GLUTEN]: "Alternative gluten free sources needed",
    },
  },
  EPA: {
    name: "Eicosapentaenoic acid (EPA)",
    info:
      "These omega-3 fatty acids are important for regulation of cholesterol levels and correct function of the immune system, joints and brain. Some genetic types do, however, experience a negative effect on the HDL cholesterol levels, which needs to be considered.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Prevents easy blood clotting, reduces triglyceride levels in the blood",
      [GeneDataGroupType.HEART]: "Helps lower the risk of heart disease",
      [GeneDataGroupType.EYE]: "Beneficial for dry eyes",
      [GeneDataGroupType.BRAIN]: "Supports learning, memory, and synoptic plasticity.",
      [GeneDataGroupType.JOINT]: "Has anti-inflammatory effects",
      [GeneDataGroupType.METABOLISM]: "Has beneficial effects on obesity",
      [GeneDataGroupType.GLUTEN]: "Alternative gluten free sources needed",
    },
  },
  SAT_FAT: {
    name: "Saturated Fats",
    info:
      'Saturated fats are one group of fats that are considered the "unhealthy fats" and can have a negative impact on heart health. Certain genetic types should pay particular attention to the saturated fat content in their diet.',
    functions: {
      [GeneDataGroupType.TOTAL]: "Involved in cholesterol metabolism",
      [GeneDataGroupType.HEART]: "Raises LDL (bad cholesterol) levels",
      [GeneDataGroupType.EYE]: "May cause risk of plaque formation",
      [GeneDataGroupType.BRAIN]: "May disrupt signaling of satiety",
      [GeneDataGroupType.JOINT]: "Weakens cartilage",
      [GeneDataGroupType.HYPERTENSION]: "Increases blood cholesterol",
      [GeneDataGroupType.METABOLISM]: "Slows down metabolism",
      [GeneDataGroupType.GLUTEN]: "Raises LDL (bad cholesterol) levels",
    },
  },
  UNSAT_FAT: {
    name: "Unsaturated fatty acids",
    info: 'This term describes all unsaturated fatty acids which are generally considered the "healthy fats".',
    functions: {
      [GeneDataGroupType.TOTAL]: "Improves blood cholesterol levels",
      [GeneDataGroupType.HEART]: "Stabilises heart rhythm",
      [GeneDataGroupType.BRAIN]: "Important for learning and memory",
      [GeneDataGroupType.JOINT]: "Reduces inflammation pain",
      [GeneDataGroupType.METABOLISM]: "Helps maintain cell membrane fluidity",
      [GeneDataGroupType.GLUTEN]: "Improves blood cholesterol levels",
    },
  },
  ARACHIDONIC_ACID: {
    name: "Arachidonic acid",
    info:
      "Arachidonic acid is part of the body's secondary messenger system and can trigger negative reactions in some people. Persons with a genetic predisposition to increased inflammatory reactions should avoid arachidonic acid.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Supports cell fluidity and protects the brain from oxidative stress",
      [GeneDataGroupType.JOINT]: "Decreases inflammation",
    },
  },
  CHOLESTEROL: {
    name: "Cholesterol",
    info:
      "The body produces cholesterol on its own but also absorbs it from our diet. A surplus of cholesterol can have a variety of negative effects on the body.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Maintain integrity and fluidity functions of cells",
      [GeneDataGroupType.HEART]: "In excess it can build up on arterie walls",
    },
  },
  FRUCTOSE: {
    name: "Fructose",
    info:
      "Fructose, also called fruit sugar, is a small molecule that can be absorbed into the bloodstream without being digested by enzymes. Some people are intolerant to this sugar and should reduce its consumption to avoid unpleasant symptoms.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Palatability and taste enhancement",
      [GeneDataGroupType.HEART]: "Excesive intake may promote the risk of fatty liver disease, heart disease, or gout.",
      [GeneDataGroupType.BRAIN]: "Damages communication between brain cells",
      [GeneDataGroupType.JOINT]: "Increases inflammation of the joints",
      [GeneDataGroupType.METABOLISM]: "Produces deleterous metabolic effects",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Provokes oxidative stress in cerebral cortex",
    },
  },
  SUGAR: {
    name: "Sugar",
    info:
      "This energy source is contained in most types of food and can lead to excessive weight gain if consumed in excessive amounts. Certain genetic types are particularly sensitive to the fat and sugar quantities in their diet and should reduce them if possible.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Provides energy",
      [GeneDataGroupType.HEART]: "Excessive intake affects arteries",
      [GeneDataGroupType.BRAIN]: "Excessive intake impairs cognitive skills",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Inceases insulin and glucose levels",
      [GeneDataGroupType.METABOLISM]: "May cause dysregulation of metabolism",
    },
  },
  LACTOSE: {
    name: "Lactose",
    info:
      "The milk sugar, lactose, contains calories and cannot be digested well by some genetic types. Depending on their genes, certain people react negatively to lactose and should reduce its consumption.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Used as an energy source by the body",
      [GeneDataGroupType.GLUTEN]: "Used as an energy source by the body",
      [GeneDataGroupType.LACTOSE]: "Used as an energy source by the body",
    },
  },
  SUCROSE: {
    name: "Sucrose",
    info:
      "This sugar contains calories and can have a huge negative impact on certain genetic types in terms of cognitive health and blood sugar regulation.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Provides energy",
      [GeneDataGroupType.BRAIN]: "Excessive intake effects memory and cognitive function",
      [GeneDataGroupType.METABOLISM]: "May influence insulin sensitivity and lipid metabolism",
    },
  },
  ALCOHOL: {
    name: "Alcohol",
    info:
      "Alcohol is a substance that can cause a variety of health-related problems if consumption is too high. Due to genetic differences, some people are more sensitive to the negative effects of excessive alcohol consumption.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Affects mood, behaviour, coordination, and concentration.",
      [GeneDataGroupType.HEART]: "Temporary increases heart rate and pressure",
      [GeneDataGroupType.BONE]: "Impacts bone density",
      [GeneDataGroupType.JOINT]: "Increases inflammation",
      [GeneDataGroupType.BRAIN]: "Decreases brain function",
      [GeneDataGroupType.HOMOCYSTEINE]: "Elevated homocysteine causes risk of DVT",
      [GeneDataGroupType.HYPERTENSION]: "Increases blood pressure",
      [GeneDataGroupType.METABOLISM]: "Slows the body's metabolism",
    },
  },
  A_LIPOIC_ACID: {
    name: "Alpha lipoic acid",
    info:
      "ALA is a strong antioxidant that helps the body neutralize toxic free radicals that would otherwise cause chain reactions of damage to cells and tissues.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Breaks down carbohydrates",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Acts as an antioxidant",
      [GeneDataGroupType.DETOX]: "Supports liver function",
    },
  },
  Q10: {
    name: "Coenzyme Q10",
    info:
      "Coenzyme Q10 is an important antioxidant that the body can produce itself, but it needs a special gene to convert it to the active form ubiquinol. People who carry a genetic defect in this gene (NQO1) are unable to convert Coenzyme Q10 to the active form.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Produces cellular energy and is an antioxidant",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Improves antioxidant enzyme activity",
    },
  },
  CYSTEINE: {
    name: "Cysteine",
    info:
      "Amino acids are the building blocks for proteins and are essential for life. Cysteine and methionine are such amino acids that can, however, have a negative impact on bone health in some genetic types.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Necessary for sulfur metabolism in the human body ",
      [GeneDataGroupType.BONE]: "Helps to maintain healthy bones",
    },
  },
  HIGH_GLYC_INDEX: {
    name: "High Glycemic Index",
    info:
      "Food types with a high glycemic index contain sugars that enter the bloodstream quickly and cause a rapid rise of the blood sugar levels. Certain genes counteract this effect, but some genetic variants can cause this system to be less effective. As a consequence, certain genetic types should avoid food types with a high glycemic index.",
    functions: {
      [GeneDataGroupType.TOTAL]: "May cause cardiovascular diseases or diabetes",
      [GeneDataGroupType.EYE]: "May damage cell function in the eye",
      [GeneDataGroupType.BRAIN]: "May lead to over eating or addiction",
      [GeneDataGroupType.METABOLISM]: "May lead to slower metabolism",
    },
  },
  COFFEE: {
    name: "Coffee",
    info:
      "Coffee contains a high dose of antioxidants and hence can be beneficial in the prevention of certain metabolic problems caused by free radicals.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Stimulant, boosting energy levels.",
      [GeneDataGroupType.HEART]: "Stimulates the intensity of heart contractions",
      [GeneDataGroupType.BONE]: "Decrease bone mineral density",
      [GeneDataGroupType.EYE]: "In excess it may effect eye health",
      [GeneDataGroupType.BRAIN]: "Increases energy metabolism in the brain",
      [GeneDataGroupType.JOINT]: "Has anti-inflammatory properties",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Decreases oxidative stress and inflammatory markers",
    },
  },
  METHIONINE: {
    name: "Methionine",
    info:
      "Amino acids are the building blocks for proteins and are essential for life. Cysteine and methionine are such amino acids that can, however, have a negative impact on bone health in some genetic types.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Supports liver detoxification and homocysteine cycle",
      [GeneDataGroupType.BONE]: "Affects bone density",
    },
  },
  PURINE: {
    name: "Purine",
    info:
      "Purines can be produced by our bodies but are also taken up in high amounts when eating animal products. Very high levels of purines can have a negative impact on joint health in certain genetic types.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Provides energy and control cell function",
      [GeneDataGroupType.JOINT]: "Provides energy and control cell function",
    },
  },
  GLUTEN: {
    name: "Gluten",
    info:
      "Gluten is a protein found primarily in wheat, barley and rye. If a person has a gluten intolerance, this protein can cause digestive problems. ",
    functions: {
      [GeneDataGroupType.TOTAL]: "Can cause an inflammatory response",
      [GeneDataGroupType.GLUTEN]: "Can cause an inflammatory response",
    },
  },
};
const geneDataGroupTypeTranslations = {
  [GeneDataGroupType.HEART]: "Heart",
  [GeneDataGroupType.BRAIN]: "Brain",
  [GeneDataGroupType.BONE]: "Bones",
  [GeneDataGroupType.JOINT]: "Joints",
  [GeneDataGroupType.EYE]: "Eyes",
  [GeneDataGroupType.HOMOCYSTEINE]: "Homocysteine",
  [GeneDataGroupType.HYPERTENSION]: "Bloodpressure",
  [GeneDataGroupType.HOMOCYSTEINE_B2]: "Vitamin B2",
  [GeneDataGroupType.GLUTEN]: "Gluten",
  [GeneDataGroupType.LACTOSE]: "Lactose",
  [GeneDataGroupType.OXIDATIVESTRESS]: "Oxidative Stress",
  [GeneDataGroupType.DETOX]: "Detoxification",
  [GeneDataGroupType.METABOLISM]: "Metabolism",
  [GeneDataGroupType.MICRONUTRIENTS]: "Micronutrients",
  [GeneDataGroupType.RDA]: "RDA",
};

export const TRANSLATIONS_EN = {
  general: {
    digital_twin: "myDigitalTwin",
    demo: "Demo",
    personalize_your_own_digital_twin: "Personalize your own digital twin!",
    personalize_your_own_digital_twin_link: "https://www.mydigitaltwin.at/",
    create_your_own_personal_digital_twin: "Create your own personal digital twin",
    cancel: "Cancel",
    logout: "Log out",
    privacy_policy: "Privacy Policy",
    got_it: "Got it",
    status_progress: "Status Progress",
    dont_show_this_again: "Do not show this again.",
    of: "of",
  },
  date_format: {
    date: "dd/MM/yyyy",
    date_time: "dd/MM/yyyy HH:mm",
  },
  icons: {
    back: "Back",
    next: "Next",
    caffeine: "Caffeine",
    fructose: "Fructose",
    gluten: "Gluten",
    lactose: "Lactose",
    blood: "Blood",
    bookmark: "Bookmark",
  },
  portal: {
    inactivity_logout: "You have been logged out due to inactivity.",
  },
  app_bar: {
    personalize_your_own_digital_twin: "Personalize your own digital twin!",
    results: "Results",
    search: "Search",
    shop: "Shop",
    profile: "Profile",
    logout: "Logout",
    favorites: "Favorites",
    your_data: "Data",
    your_reports: "Reports",
    logout_modal: {
      heading: "Do you really want to log out?",
    },
  },
  gender: {
    [Gender.MALE]: "Male",
    [Gender.FEMALE]: "Female",
  },
  ethnicity: {
    [Ethnicity.AFRICAN]: "African",
    [Ethnicity.ARABIC]: "Arabic",
    [Ethnicity.ASIAN]: "Asian",
    [Ethnicity.EUROPEAN]: "European",
    [Ethnicity.HISPANIC]: "Hispanic",
    [Ethnicity.MIXED]: "Mixed",
  },
  diet_preference: {
    [DietPreference.STANDARD]: "Standard",
    [DietPreference.PESCATARIAN]: "Pescatarian",
    [DietPreference.VEGETERIAN]: "Vegetarian",
    [DietPreference.VEGAN]: "Vegan",
  },
  login: {
    welcome: "Welcome",
    sign_in: "Sign In",
    sign_up: "Sign Up",
    log_in: "Log in",
    reset_password_action: "Reset",
    forgot_password: "Forgot Password?",
    visit_our_shop: "Visit our Shop",
    register_email_info: "If you have purchased our DNA kit already, please use the same e-mail address.",
    code_info: "We've sent you a code via e-mail. Please enter it here.",
    reset_password_info: "We sent you a link to reset your password via e-mail.",
    change_password: "Change Password",
    change_password_action: "Change",
    change_password_info: "Your password was changed successfully!",
    resend_code: "Resend Code",
    fields: {
      email: "Your e-mail",
      password: "Password",
      repeat_password: "Repeat Password",
      firstname: "First name",
      lastname: "Last name",
    },
    errors: {
      email_already_registered: "This e-mail is already registered!",
      email_invalid: "This is not a valid e-mail!",
      password_too_short: "Min. 8 characters!",
      passwords_dont_match: "Passwords do not match!",
    },
  },
  onboarding: {
    introduction: {
      header: "Hello!\nI'm gonna be your digital twin.",
      description: "Please enter the following data so that I am as similar to you as possible.", // TODO: Translate properly
      button: "Let's start!",
    },
    firstname: {
      label: "First Name",
      header: "What is your first name?",
    },
    lastname: {
      label: "Last Name",
      header: "What is your last name?",
    },
    gender: {
      label: "Gender",
      header: "What is your gender?",
    },
    weight: {
      label: "Weight (kg)",
      header: "What is your weight (kg)?",
    },
    date_of_birth: {
      label: "Date of Birth",
      header: "What is your date of birth?",
    },
    height: {
      label: "Height (cm)",
      header: "What is your height (cm)?",
    },
    ethnicity: {
      label: "Ethnicity",
      header: "What is your ethnicity?",
    },
    diet_preference: {
      label: "Diet Preference",
      header: "What is your diet preference?",
    },
    next: {
      button: "Next",
    },
  },
  api_errors: {
    OTHER: "An unknown error occurred!",
    user: {
      INTERNAL_SERVER_ERROR: "An unknown error occurred!",
    },
    login: {
      [LoginError.CREDENTIALS_INVALID]: "Invalid e-mail or password!",
      [LoginError.UNKNOWN_USER]: "This e-mail is not registered!",
      [RegisterError.INVALID_DATA]: "Check your input!",
      [RegisterError.EMAIL_ALREADY_IN_USE]: "This e-mail is already registered!",
      [VerifyCodeError.CODE_INVALID]: "Code is invalid or expired!",
      [PasswordResetError.UNKNOWN_USER]: "This e-mail is not registered!",
      ACCESS_DENIED: "Access denied!",
      INTERNAL_SERVER_ERROR: "An unknown error occurred!",
    },
    update_password: {
      [UpdatePasswordError.INVALID_PASSWORD]: "Old password is invalid.",
    },
  },
  menu_item_category: {
    [MenuItemCategory.RECOMMENDATIONS]: "Your Recommendations",
    [MenuItemCategory.ORGANS]: "Organs",
    [MenuItemCategory.DIET]: "Diet",
    [MenuItemCategory.METABOLISM]: "Metabolism",
    [MenuItemCategory.BLOOD]: "Blood",
    [MenuItemCategory.MENTAL_HEALTH]: "Mental Health",
    [MenuItemCategory.MICRONUTRIENTS]: "Micronutrients",
    [MenuItemSubcategory.GENEVARIATIONS]: "Gene variations",
    [MenuItemCategory.USER]: "Profile",
  },
  gene_data_group_area: {
    [GeneDataGroupArea.ORGANS]: "Organs",
    [GeneDataGroupArea.DIET]: "Nutrition",
    [GeneDataGroupArea.METABOLISM]: "Metabolism",
    [GeneDataGroupArea.BLOOD]: "Blood",
    [GeneDataGroupArea.MICRONUTRIENTS]: "Micronutrients",
  },
  menu_item_subcategories: {
    ...geneDataGroupTypeTranslations,
    MICRONUTRIENTS_TO_INCREASE: "Micronutrients",
    OVERVIEW: "Overview",
    FINDINGS: "Findings",
    GENEVARIATIONS: "Gene Variations",
    FRUCTOSE: "Fruktose",
    USER_DATA: "Your Data",
    RESILIENCE: "Resilience",
    navigate_to: {
      [GeneDataGroupType.HEART]: "Go to Heart",
      [GeneDataGroupType.BRAIN]: "Go to Brain",
      [GeneDataGroupType.BONE]: "Go to Bones",
      [GeneDataGroupType.JOINT]: "Go to Joints",
      [GeneDataGroupType.EYE]: "Go to Eyes",
      [GeneDataGroupType.HOMOCYSTEINE]: "Go to Homocysteine",
      [GeneDataGroupType.HYPERTENSION]: "Go to Bloodpressure",
      [GeneDataGroupType.HOMOCYSTEINE_B2]: "Go to Vitamin B2",
      [GeneDataGroupType.GLUTEN]: "Go to Gluten",
      [GeneDataGroupType.LACTOSE]: "Go to Lactose",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Go to Oxidative Stress",
      [GeneDataGroupType.DETOX]: "Go to Detoxification",
      [GeneDataGroupType.METABOLISM]: "Go to Metabolism",
      [GeneDataGroupType.MICRONUTRIENTS]: "Go to Micronutrients",
      [GeneDataGroupType.RDA]: "Go to RDA",
      MICRONUTRIENTS_TO_INCREASE: "Go to Micronutrients",
      GENEVARIATIONS: "Go to Gene Variations",
      RESILIENCE: "Go to Resilience",
    },
  },
  gene_data_group_type: geneDataGroupTypeTranslations,

  gene: {
    orientation: "Orientation",
    name: "Symbol",
    refsnp: "rsNCBI",
    genotype: "Geno Type",
    genoTypeNoSpace: "Genotype",
  },
  nutrients_to_increase: {
    heading: "Micronutrients",
    subheading:
      "Based on your genetic profile, we have identified here the most critical nutrients, which can have a positive impact on your overall level of health. For each system in your body, a specific nutrient recommendation is available, based on their relevance to that system.",
  },
  nutrients_to_decrease: {
    heading: "Micronutrients",
    subheading:
      "Based on your genetic profile, we have identified here the most critical nutrients, which can have a negative impact on your overall level of health. For each system in your body, a specific nutrient recommendation is available, based on their relevance to that system.",
  },
  micronutrient: {
    name: "Micronutrient",
    recommendation: "RDA",
    value: "Your RDA",
    unit: "Unit",
    of: "of",
    not_specified: "Not Specified",
    rda_not_specified: "RDA not specified",
    disposition: "Disposition",
    explanation: {
      heading: "Information on nutrients that you are recommended to consume more or less of look like this",
      hero: {
        card: {
          header: "Vitamin C",
          line_1: "Reduces free-radical damage",
          line_2: "Kakadu plums, Acerola cherries",
          line_3: "240 mg (300% of Ø 80 mg)",
          bottom_line_1: "Significance",
          bottom_line_2: "Important",
          bottom_line_3: "Current value",
          bottom_line_4: "Slight deficiency",
        },
        descriptions: {
          top_left: {
            line_1: "The main function of the",
            line_2: "nutrient (&nbsp;&nbsp;&nbsp;) and foods",
            line_3: "where it occurs in",
            line_4: "higher amounts (&nbsp;&nbsp;&nbsp;)",
            line_1_x: "50",
            line_2_x: "63",
            line_3_x: "91",
            line_4_x: "78",
            nutrient_icon: {
              x: "118",
              y: "21",
            },
            chef_icon: {
              x: "182.5",
              y: "55",
            },
          },
          top_center: {
            line_1: "It’s about this nutrient.",
          },
          top_right: {
            line_1: "You can find out ",
            line_1_x_mobile: "190",
            line_2: "more here.",
            line_2_x_mobile: "190",
          },
          center_left: {
            line_1: "Your personal daily ",
            line_2: "requirement (&nbsp;&nbsp;&nbsp;) compared",
            line_3: "to the typical Recommended",
            line_4: "Daily Allowance (RDA).",
            line_1_x: "80",
            line_2_x: "33",
            line_3_x: "21.5",
            line_4_x: "55",
            cutlery_icon: {
              x: "116",
              y: "123",
            },
          },
          center_right: {
            line_1: "The result of your ",
            line_2: "blood analysis shows you ",
            line_3: "whether there is ",
            line_4: "a deficiency (&nbsp;&nbsp;&nbsp;) or",
            line_5: "a sufficient amount (&nbsp;&nbsp;&nbsp;)",
            line_6: "and how this value",
            line_7: "has changed since ",
            line_8: "the last blood test (&nbsp;&nbsp;&nbsp;).",
            icon_1: {
              cx: "88",
              cy: "61",
            },
            icon_2: {
              cx: "132",
              cy: "78.5",
            },
            icon_3: {
              cx: "124",
              cy: "129",
            },
          },
          bottom_left: {
            line_1: "Your genetic analysis",
            line_2: "shows you how significant",
            line_3: "it is to increase or",
            line_4: "reduce the nutrient.*",
            line_1_x: "65",
            line_2_x: "34",
            line_3_x: "86",
            line_4_x: "71",
          },
          bottom_center: {
            line_1: "* The significance of the nutrient is",
            line_2: "  specific to the area of the body in focus.",
          },
          bottom_right: {
            line_1: "Falling Trend",
            line_2: "Rising Trend",
          },
        },
        mobile_descriptions: {
          top_left: {
            line_1: "Your genetic",
            line_2: "analysis shows ",
            line_3: "whether you tend",
            line_4: "to need more ",
            line_5: "or less of",
            line_6: "the nutrient.*",
          },
          top_right: {
            line_1: "The result of your ",
            line_2: "blood analysis shows you ",
            line_3: "whether there is ",
            line_4: "a deficiency (&nbsp;&nbsp;&nbsp;) or",
            line_5: "a sufficient amount (&nbsp;&nbsp;&nbsp;)",
            line_6: "and how this value",
            line_7: "has changed since ",
            line_8: "the last  ",
            line_9: "blood test (&nbsp;&nbsp;&nbsp;).",
            icon_1: {
              cx: "80",
              cy: "164",
            },
            icon_2: {
              cx: "90",
              cy: "69",
            },
            icon_3: {
              cx: "137",
              cy: "88",
            },
          },
          middle_left: {
            line_1: "* The significance of the",
            line_2: "nutrient is specific",
            line_3: "to the area of",
            line_4: "the body in focus.",
          },
          bottom_1: {
            line_1: "The main function of the nutrient (&nbsp;&nbsp;&nbsp;)",
            line_2: "and foods where it occurs ",
            line_3: "in higher amounts (&nbsp;&nbsp;&nbsp;).",
            wrench_x: "222",
            wrench_y: "127.5",
            chef_hat_x: "124",
            chef_hat_y: "167",
          },
          bottom_2: {
            line_1: "Your personal daily requirement (&nbsp;&nbsp;&nbsp;)",
            line_2: "compared to the typical",
            line_3: "Recommended Daily Allowance (RDA).",
            cutlery_x: "216",
            cutlery_y: "210",
          },
        },
      },
    },
  },
  overview: {
    heading: "Welcome to myDigitalTwin!", //"Welcome {{firstname}} {{lastname}}!",
    subheading_1:
      "Your personalised digital twin is created using your unique genetic profile. Your genes provide valuable insights that you can use to stay healthy. The analysis covers different systems within your body, from the brain to metabolism. The avatar will help illustrate the different areas of the body in focus and can be rotated to give you different perspectives. Use the menu on the left and begin your journey to discovering good health.",
    subheading_2: "",
    subheading_3:
      "To get you started, we thought you might like to know about your risk of having or developing an intolerance to gluten, lactose, or caffeine. Even a high risk doesn’t mean you have an intolerance, but if you have discomfort, you may wish to consult a doctor.",
    subheading_demo_1:
      "Here you can find the example results for Mica, our demo digital twin. Mica is created using their own unique genetic profile – if you want to get your own digital twin, order your DNA test kit here today.",
    subheading_demo_2:
      "Your genes provide valuable insights that you can use to stay fit and healthy. The digital avatar will help visualise the different areas of your body in focus. It can be rotated to give you different perspectives too. Use the navigation to begin your journey of discovery.",
    subheading_demo_3:
      "To get you started, we thought you might like to know Mica’s genetic risk of having or developing an intolerance to gluten and lactose, and Mica’s efficiency at regulating caffeine in the body.\n",
    percent_of_population: "{{percent}}% of the population",
    caffeine: {
      name: "Caffeine\nRegulation",
      breakdown: {
        [CaffeineBreakdown.NORMAL]: "Normal Efficiency",
        [CaffeineBreakdown.SLOW]: "Low Efficiency",
      },
    },
    lactose: {
      name: "Lactose\nIntolerance",
      intolerance: {
        [Intolerance.HIGH_RISK]: "Very High Risk",
        [Intolerance.NO_RISK]: "No Increased Risk",
      },
    },
    gluten: {
      name: "Gluten\nIntolerance",
      intolerance: {
        [Intolerance.HIGH_RISK]: "High Risk",
        [Intolerance.MODERATE_RISK]: "Moderate Risk",
        [Intolerance.NO_RISK]: "Low Risk",
      },
    },
    arrow_text: "You can learn more in the section on diet.",
  },
  gene_variations: {
    heading: "Your Gene Analysis",
    subheading: "Below you can see which genes were analysed, your genotype and how they relate to your body:",
    types: {
      [GeneDataGroupType.HEART]: "Heart",
      [GeneDataGroupType.BRAIN]: "Brain",
      [GeneDataGroupType.BONE]: "Bones",
      [GeneDataGroupType.JOINT]: "Joints",
      [GeneDataGroupType.EYE]: "Eyes",
      [GeneDataGroupType.HOMOCYSTEINE]: "Homocysteine",
      [GeneDataGroupType.HYPERTENSION]: "Bloodpressure",
      [GeneDataGroupType.HOMOCYSTEINE_B2]: "Homocysteine B2",
      [GeneDataGroupType.GLUTEN]: "Gluten",
      [GeneDataGroupType.LACTOSE]: "Lactose",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Oxidative Stress",
      [GeneDataGroupType.DETOX]: "Detoxification",
      [GeneDataGroupType.METABOLISM]: "Metabolism",
      [GeneDataGroupType.MICRONUTRIENTS]: "Micronutrients",
      [GeneDataGroupType.RDA]: "RDA",
    },
    page_1: {
      header: "Your Genes",
      explanation_1:
        "All human cells consist of chromosomes, which contain our genetic information in the form of spiral DNA strands. Certain sequences of DNA are defined as genes. Depending on the structure of the gene, there may be higher risks for diseases. These risks can be determined with the help of gene analyses.",
      explanation_2:
        "While your entire genome, i.e. the sum of all DNA sequences, is unique, individual genes can be identical to those from different people.",
      nucleus:
        "In the nucleus of our 50 trillion cells are 23 inherited pairs of chromosomes. Each chromosome contains a spiral DNA strand, also known as the double helix.",
      double_helix:
        "The double helix consists of four nucleic bases. Only A-T and C-G connections can form in the process.",
      dna:
        "Within DNA, certain sequences form genes. They instruct the cell which proteins to form and thus which functions to perform.",
      adenin: "Adenine",
      cytocin: "Cytocin",
      thymin: "Thymine",
      guanin: "Guanine",
    },
    page_2: {
      explanation:
        "Genes form the building blocks of life, a detailed instruction manual for how to build and maintain every system in the body. Over the past years we have learned a great deal about genes and their various roles and functions, and our understanding continues to grow.",
      body_1:
        "On the following page we have listed the nutrition relevant genes that have been analysed in relation to different systems in your body.",
      body_2: "Information about your genes is shown like this:",
      hero: {
        mobile: {
          bottom_1: {
            line_1: "The genotype describes the genetic",
            line_2: "make-up, i.e. the structure of the gene.",
          },
          bottom_2: {
            line_1: "The symbols show you in which",
            line_2: "direction the gene was sequenced.",
          },
          bottom_right: {
            line_1: "The SNP number is ",
            line_2: "a biological marker located ",
            line_3: "on or near the gene,",
            line_4: "which is being analysed.",
          },
        },
        right_top: {
          line_1: "You can find",
          line_2: "out more here.",
        },
        right_middle: {
          line_1: "This value shows",
          line_2: "how popular your",
          line_3: "genotype is within",
          line_4: "the population.",
        },
        left_top: {
          line_1: "The symbols show you in",
          line_2: "which direction the",
          line_3: "gene was sequenced.",
        },
        left_middle: {
          line_1: "Forward",
          line_2: "Reverse",
        },
        bottom_left: {
          line_1: "The genotype describes the",
          line_2: "genetic make-up,",
          line_3: "i.e. the structure of the gene.",
        },
        bottom_right: {
          line_1: "The SNP number is a biological",
          line_2: "marker located on or near the gene,",
          line_3: "which is being analysed.",
        },
        top_middle: "It’s about this gene.",
        genotype: "Genotype",
      },
    },
  },
  nutrient: {
    increase: "Increase",
    neutral: "Neutral",
    decrease: "Decrease",
    sources: "Sources",
    source_values: nutrientSourcesEN,
    values: {
      ...nutrientTranslations,
      Folsäure: nutrientTranslations.FOLIC_ACID,
      VitaminA: nutrientTranslations.VIT_A,
      VitaminB2: nutrientTranslations.VIT_B2,
      VitaminB6: {
        ...nutrientTranslations.VIT_B6,
        name: "Vitamin B6",
        info: "",
      },
      VitaminB12: {
        ...nutrientTranslations.VIT_B6,
        name: "Vitamin B12",
        info: "",
        functions: {
          [GeneDataGroupType.TOTAL]: "Supports RBC and DNA production",
          [GeneDataGroupType.BRAIN]: "Prevents neuron loss and brain atrophy",
        },
      },
      VitaminC: nutrientTranslations.VIT_C,
      VitaminD3: nutrientTranslations.VIT_D3,
      VitaminE: nutrientTranslations.VIT_E,
      Eisen: nutrientTranslations.IRON,
      Kalzium: nutrientTranslations.CALCIUM,
      Kupfer: nutrientTranslations.COPPER,
      Magnesium: nutrientTranslations.MAGNESIUM,
      Mangan: nutrientTranslations.MANGANESE,
      Selen: nutrientTranslations.SELENE,
      Zink: nutrientTranslations.ZINC,
      Lutein: nutrientTranslations.LUTEINE,
      MSM: nutrientTranslations.MSM,
      Phytosterol: nutrientTranslations.PHYTOSTEROL,
      Alpha_Liponsäure: nutrientTranslations.A_LIPOIC_ACID,
      Alpha_Linolensäure: nutrientTranslations.A_LINOLENIC_ACID,
      Omega3: nutrientTranslations.OMEGA3,
      Coenzym_Q10: nutrientTranslations.Q10,
    },
    disposition: {
      recommendation: "Significance",
      importance_level: {
        neutral: "Neutral",
        slightly: "Slightly important",
        moderately: "Moderately important",
        important: "Important",
        very_important: "Very important",
        essential: "Essential",
      },
    },
    functions: {
      [GeneDataGroupType.BONE]: "Bones",
      [GeneDataGroupType.BRAIN]: "Brain",
      [GeneDataGroupType.DETOX]: "Detox",
      [GeneDataGroupType.EYE]: "Eyes",
      [GeneDataGroupType.GLUTEN]: "Gluten",
      [GeneDataGroupType.HEART]: "Heart",
      [GeneDataGroupType.HOMOCYSTEINE]: "Homocycteine",
      [GeneDataGroupType.HOMOCYSTEINE_B2]: "Homocycteine B2",
      [GeneDataGroupType.HYPERTENSION]: "Hypertension",
      [GeneDataGroupType.JOINT]: "Joint",
      [GeneDataGroupType.LACTOSE]: "Lactose",
      [GeneDataGroupType.METABOLISM]: "Metabolism",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Oxidative Stress",
    },
  },
  twin_communication: {
    link_to_genes: "Review the gene analysed",
    result_heading: "Your Result",
    bad_image_description: "Your Result",
    good_image_active_description: "Your Result",
    good_image_inactive_description: "Optimal",
    answer: {
      [FeedbackType.STORYDATA]: {
        heading: "Your Gene Results",
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        heading: "Recommendation",
      },
      [FeedbackType.GENES]: {
        heading: "My genes",
      },
      [FeedbackType.NUTRIENTS]: {
        heading: "Recommendation",
        description:
          "Based on your genes and their associated strengths and weaknesses you should increase or decrease certain foods and nutrients, with general RDA values as a basis. These recommendations are calculated based on your genetic profile.",
      },
      [FeedbackType.NUTRIENTS_TO_INCREASE]: {
        heading: "Micronutrients",
        description:
          "Based on your genes and their associated strengths and weaknesses you should increase certain foods and nutrients, with general RDA values as a basis. These recommendations are calculated based on your genetic profile.",
        empty: "There are no nutrients you need to increase.", // TODO: Proofread
      },
      [FeedbackType.NUTRIENTS_TO_DECREASE]: {
        heading: "Micronutrients",
        description:
          "Based on your genes and their associated strengths and weaknesses you should decrease certain foods and nutrients, with general RDA values as a basis. These recommendations are calculated based on your genetic profile.",
        empty: "There are no nutrients you need to decrease.", // TODO: Proofread
      },
      [FeedbackType.INTRODUCTION]: {
        heading: "Introduction",
      },
      [FeedbackType.RESILIENCE]: {
        heading: "Resilience",
        description_1:
          "Our mental health has a great impact on our quality of life. If we do not have the ability to adapt to change (resilience) and are stressed in the long term, this can affect our physical health.",
        description_2:
          "Our resilience is not only influenced by experiences, but also by biological factors, including our genes.",
      },
      [FeedbackType.MICRONUTRIENTS]: {
        heading: "Your Recommended Daily Allowance",
        description:
          "Based on your genes and their associated strengths and weaknesses you should increase or decrease certain foods and nutrients, with general RDA values as a basis. These recommendations are calculated based on your genetic profile.",
      },
    },
    additional_texts: {
      [FeedbackType.STORYDATA]: {
        heading: "Did you know?",
        positive: "Yes, tell me more!",
        negative: "No, thank you!",
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        heading: "Recommendation",
        positive: "Yes, I do!",
        negative: "No, thank you!",
      },
      [FeedbackType.GENES]: {
        heading: "Did you know?",
        positive: "Yes, tell me more!",
        negative: "No, thank you!",
      },
      [FeedbackType.NUTRIENTS]: {
        heading: "Recommendation",
        positive: "Yes, I do!",
        negative: "No, thank you!",
      },
      [FeedbackType.INTRODUCTION]: {
        heading: "Introduction",
        positive: "Yes, I do!",
        negative: "No, thank you!",
      },
    },

    TRIG_INFORMATION: {
      header: "Triglycerides",
      [FeedbackType.STORYDATA]: {
        question:
          "Triglycerides are an indespensable energy store for the body, but too much can be negative for cardiac health. Would you like to know if you can regulate triglycerides normally?",
        description:
          "Triglycerides (like cholesterol) are among the blood lipids and are indispensable as an energy store for the body. When the building blocks for triglycerides are increased in food, the body produces an excess of triglycerides. Excessive consumption of dietary fats may therefore lead to increased levels of triglycerides, which may have a negative effect on cardiac health. In some people, however, genetic variations can also lead to elevated triglyceride levels.",
        GOOD:
          "Good news! Your body can regulate triglycerides normally, therefore you do not have a predisposition to elevated triglyceride levels.",
        GOOD_list: [
          "Triglycerides can be consumed in the diet.",
          "Triglycerides are also produced by the body.",
          "Your ability to regulate triglycerides is normal.",
          "This means triglycerides are being reduced.",
          "The result is triglyceride levels are normal.",
        ],
        BAD: "Due to your genetic variation, you have a predisposition to elevated triglyceride levels.",
        BAD_list: [
          "Triglycerides can be consumed in the diet.",
          "Triglycerides are also produced by the body.",
          "Your ability to regulate triglycerides is impaired.",
          "This means triglycerides are not being reduced sufficiently.",
          "This can result in triglyceride levels being too high.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Would you like a recommendation based on your ability to regulate triglyceride levels?",
        GOOD:
          "Your body can regulate your triglycerides normally and you are not predisposed to increased values. Nevertheless, please make sure to consume high-quality fats from fish like salmon and stay active and do sports on a regular basis. ",
        BAD:
          "Due to your genetic variation, unfortunately you have a predisposition to increased tricyceride levels. You can counteract this by eating a low-sugar diet and avoiding alcohol. If you like fish, be sure to eat salmon, herring, or sardines, for example, which contain good fats. Also, be active and do a lot of exercise and sports on a regular basis. ",
      },
    },
    CHOL_INFORMATION: {
      header: "Cholesterol",
      [FeedbackType.STORYDATA]: {
        question:
          "Cholesterol is a vital blood fat and its regulation in the body can be effected by your genes. Would you like to know if your genes help or hinder your ability to regulate cholesterol?",
        description:
          'Cholesterol is a vital blood fat and an important building block for numerous metabolic processes. A distinction is made between "good" (HDL) and "bad" (LDL) cholesterol. Cholesterol is produced both by the body itself and also absorbed from food. The amount is crucial, because bad cholesterol levels are associated with a variety of health problems. The regulation of cholesterol levels involves several genes that may be impaired in their function by genetic variations.',
        GOOD:
          "Looking good! Your body can regulate cholesterol normally, therefore you do not have a predisposition to high cholesterol levels.",
        GOOD_list: [
          "Cholesterol can be consumed in the diet.",
          "Cholesterol is also produced by the body.",
          "Your ability to regulate cholesterol levels is normal.",
          "This means cholesterol is being reduced.",
          "The result is cholesterol levels are normal.",
        ],
        BAD: "Due to your genetic variation, you have a predisposition to high cholesterol levels.",
        BAD_list: [
          "Cholesterol can be consumed in the diet.",
          "Cholesterol are also produced by the body.",
          "Your ability to regulate cholesterol levels is impaired.",
          "This means cholesterol is not being reduced sufficiently.",
          "This can result in cholesterol levels being too high.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Would you like a recommendation based on your ability to regulate cholesterol levels?",
        GOOD:
          "Congratulations! Nevertheless, do yourself something good by exercising regularly and eating a healthy diet.",
        BAD:
          "Do sport and exercise regularly and reduce your alcohol, nicotine and meat consumption. You should also adopt a low-fat and low-sugar diet.",
      },
    },
    OMEGA3_INFORMATION: {
      header: "OMEGA-3",
      [FeedbackType.STORYDATA]: {
        question:
          'OMEGA-3 fatty acids can help lower "bad" Cholesterol and increase "good" Cholesterol, however, this depends on your genes. Would you like to learn if OMEGA-3\'s can help you lower bad Cholesterol?',
        description:
          'Omega-3 fatty acids play a central role in the human body\'s metabolism. In addition to possessing many other positive qualities, they can improve cardiac health. Studies have shown that omega-3 fatty acids can have a positive effect on cholesterol levels by reducing "bad" LDL cholesterol levels and increasing "good" HDL cholesterol. However, this effect can be altered by a genetic variation in the APOA1 gene.',
        GOOD:
          "Looks great! Your APOA1 gene functions normally, so your HDL cholesterol levels are being improved by omega-3 fatty acids.",
        GOOD_list: [
          "Omega-3 fatty acids (DHA/EPA) can be consumed in the diet.",
          "HDL(“good” cholesterol) is produced by the body.",
          "Your APOA1 gene functions normally.",
          "This means Omega - 3 fatty acids have a positive influence on HDL levels.",
          "The result is HDL cholesterol levels are being increased.",
        ],
        BAD:
          "Due to your genetic variation, your APOA1 gene’s function is impaired, so your HDL cholesterol levels are not being improved by omega-3 fatty acids.",
        BAD_list: [
          "Omega-3 fatty acids (DHA/EPA) can be consumed in the diet.",
          "HDL(“good” cholesterol) is produced by the body.",
          "Your APOA1 gene’s function is impaired.",
          "This means Omega - 3 fatty acids do not have a positive influence on HDL levels.",
          "The result is HDL cholesterol levels are being reduced.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Would you like a recommendation based on the effectiveness of Omega-3 fatty acids to regulate your cholesterol levels?",
        GOOD:
          "Supplement your food with high-quality omega3 oils, such as linseed oil, camelina oil and hemp oil. If you have an Omega3 deficiency or a low HDL level, you can also take a high-quality Omega3 supplement.",
        BAD:
          "You should consider using high-quality omega3 oils in the kitchen, but an additional intake of omega3 supplements are not able to improve your HDL values.",
      },
    },
    CALCIUM_INFORMATION: {
      header: "Calcium",
      [FeedbackType.STORYDATA]: {
        question:
          "The absorbtion of calcium from your diet in your body can be effected by your genes. Calcium is important for bone health. Would you like to learn if you can sufficiently absorb calcium from your diet?",
        description:
          "Calcium is an important mineral that is absorbed through the diet. A sufficient amount of calcium doesn't just promotes stability and strength of the bones but also plays a role in other important body functions. A common genetic variation in the LCT gene may result in decreased calcium absorption from one's diet. As a result, the body falls back and uses its reserves in the skeleton, thus harming the health of the bones. An increased intake of calcium-rich foods can counteract this effect.",
        GOOD: "Super! You are able to absorb calcium effectively from your diet, which is important for bone health.",
        GOOD_list: [
          "Calcium can be consumed in the diet.",
          "Calcium is absorbed effectively.",
          "This means sufficient calcium absorption is possible.",
        ],
        BAD:
          "Due to your genetic variation, your LCT gene may result in the reduced absorption of calcium from your diet.",
        BAD_list: [
          "Extra calcium can be supplemented in your diet.",
          "This is to compensate for the reduced absorption of calcium.",
          "The result would be a sufficient intake of calcium.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Would you like a recommendation based on your ability to absorb calcium from your diet?",
        GOOD:
          "Your body is able to absorb calcium effectively. An increased intake  of calcium is therefore not necessary.",
        BAD:
          "Your body can handle an extra helping of calcium. To ensure good bone health, you should consider supplementing calcium as part of a healthy diet.",
      },
    },
    VDR_INFORMATION: {
      header: "Vitamin D",
      [FeedbackType.STORYDATA]: {
        question:
          "Vitamin D is produced in the body with the help of the sun. In its active form and bound to the Vitamin D receptor it helps the body absorb calcium. Would you like to know if you can bind Vitamin D normally?",
        description:
          "Vitamin D can be produced by the body with the help of solar radiation, but also can be ingested through the diet. In addition to numerous important tasks, vitamin D controls the absorption of calcium and leads to healthy bones. For vitamin D to be effective, it must first be converted into its active form and bind to the vitamin D receptor (VDR). This receptor is found in almost every body cell and can only fulfill its health-beneficial tasks in its bound form. Genetic variations can alter the receptor's structure.",
        GOOD: "Here comes the sun! Your Vitamin D Receptor (VDR) can bind with vitamin D normally.",
        GOOD_list: [
          "Vitamin D in a normal dose.",
          "Your Vitamin D receptors work normally.",
          "This means you can benefit from the positive vitamin D effect.",
        ],
        BAD:
          "Due to your genetic variation, your Vitamin D Receptor (VDR) has an altered structure leading to a poor ability to bind with vitamin D.",
        BAD_list: [
          "Vitamin D in a high dose.",
          "Your Vitamin D receptor is defective.",
          "This means additional vitamin D is required to compensate to have its positive effect.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Would you like a recommendation based on the effectiveness of your Vitamin D receptor?",
        GOOD:
          "Your body does not have an increased need for vitamin D3. Nevertheless, you might consider having your Vitamin D3 level checked in order to rule out a possible deficiency.",
        BAD:
          "Your body needs an increased amount of vitamin D3. Sunbathing alone is definitely not enough here. You should consider having your Vitamin D3 level checked and then supplement your diet with Vitamin D3 to get to the appropriate amount.",
      },
    },
    OXI_INFORMATION: {
      header: "Free radicals",
      [FeedbackType.STORYDATA]: {
        question:
          "Harmful free radicals destroy cells and can promote the aging process. It's known as oxidative stress. Would you like to learn if you can sufficiently neutralise these free radicals?",
        description:
          "When converting energy, harmful by-products, so-called free radicals, are created. These destroy cells and promote the aging process. However, the body has a system to neutralize these free radicals and render them harmless. This protection is controlled by several genes. Yet, common gene variations can interfere with this function and increase oxidative stress.",
        GOOD:
          "No stress! Free radicals are being sufficiently neutralised, which means you don’t have increased oxidative stress. The normal does of antioxidants is sufficient.",
        GOOD_list: [
          "Your genes produce sufficient protection against free radicals.",
          "This means most of the free radicals are neutralised.",
          "Other antioxidants can be used to protect from free radicals.",
        ],
        BAD:
          "Due to your genetic variation, free radicals cannot be neutralised sufficiently, meaning you have increased oxidative stress. An increased quantity of antioxidants restores protection. ",
        BAD_list: [
          "Your genes produce too little protection against free radicals.",
          "Other antioxidants can be used to protect from free radicals.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Would you like a recommendation based on your ability to neutralise free radicals?",
        GOOD:
          "Congratulations! Your body is able to neutralize free radicals sufficiently. Nevertheless, be careful when dealing with nicotine, alcohol and other harmful substances. Your body will thank you! And treat yourself to an extra portion of fruit and vegetables on a regular basis.",
        BAD:
          "Make sure that you supplement your diet with important antioxidants such as Vitamin C, Vitamin E, Coenzyme Q10 and selenium as nutrient preparations and increase your consumption of fresh fruit and vegetables. Avoid nicotine and reduce your alcohol consumption. Wherever you can reduce stress, do it!",
      },
    },
    HOMO_INFORMATION: {
      header: "Homocysteine",
      [FeedbackType.STORYDATA]: {
        question:
          "When proteins are broken down in the body, homocysteine, a powerful cytotoxin, is produced which can harm your health. Would you like to know if you can breakdown homocysteine sufficiently?",
        description:
          "Homocysteine is a substance that is produced in the body during the breakdown of proteins. Since homocysteine is a potent cytotoxin, it must be rapidly broken down, for which vitamins B6, B12 and folic acid are important. Part of this mechanism involves the MTHFR gene. Frequently occurring genetic variations, however, may result in this gene not functioning properly and thus limiting the breakdown of homocysteine. In many cases this leads to an increased homocysteine level in the blood.",
        GOOD:
          "All good here! The activity of your MTHFR gene is not limited, so you can reduce homocysteine sufficiently.",
        GOOD_list: [
          "Your genes can produce the MTHFR enzyme, which provides protection against homocysteine.",
          "The result is homocysteine is being broken down.",
        ],
        BAD:
          "Due to your genetic variation, the activity of your MTHFR gene is limited, which means it cannot reduce homocysteine sufficiently.",
        BAD_list: [
          "Your genes do not provide protection against homocysteine.",
          "This means additional micronutrients can be taken.",
          "The result would be homocysteine is being broken down.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Would you like a recommendation based on your ability to break down homosysteine levels in your body?",
        GOOD:
          "Very good! Your homocysteine levels should be fine. If you still want to do something good for yourself, do sport and exercise regularly and eat healthy!",
        BAD:
          "Please refrain from taking folic acid. Instead, take 5-MTHF (bioactive form of folate) if you have a folic acid deficiency. Other nutrients to lower homocysteine are Vitamin B6 and Vitamin B12. Do endurance sports regularly, reduce excess weight if necessary, eat a lot of green vegetables and cut down on animal proteins. Avoid nicotine and other environmental toxins.",
      },
    },
    VITB2HOMO_INFORMATION: {
      header: "Vitamin B2",
      [FeedbackType.STORYDATA]: {
        question:
          "Vitamin B2 can assist your body to break down homocsteine, depending on your genes. Would you like to know if Vitamin B2 can help you lower homocysteine levels?",
        description:
          "Vitamin B2 is not only essential for the energy supply of the cells, but also supports the MTHFR gene in the decomposition of homocysteine. Increased homocysteine levels are associated with a variety of health problems. In combination with the right genes, vitamin B2 is able to lower this value.",
        GOOD: "Good to know! Vitamin B2 has a lowering effect on homocysteine levels in your body.",
        GOOD_list: [
          "Vitamin B2 can be supplemented in the diet.",
          "This would reduce homocysteine in your body.",
          "The result would be lower homocysteine levels.",
        ],
        BAD: "Due to your genetic variation, Vitamin B2 does not have a lowering effect on homocysteine.",
        BAD_list: [
          "Vitamin B2 can be supplemented in the diet.",
          "However, it would have no effect on your homocysteine levels.",
          "This means homocysteine levels are not being reduced.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Would you like a recommendation based on the effectiveness of Vitamin B2 to reduce homocysteine levels in your body?",
        GOOD:
          "To lower the homocysteine level, you should supplement vitamin B2 as a nutrient. If you have an elevated homocysteine level, please do not use nicotine and reduce your alcohol consumption. Make sure you manage your stress well and don't overdo it with your coffee consumption.",
        BAD:
          "Instead of vitamin B2, you can supplement vitamin B6, B12 and folate to lower homocysteine levels. If you have an elevated homocysteine level, please do not use nicotine and reduce your alcohol consumption. Make sure you manage your stress well and don't overdo it with your coffee consumption.",
      },
    },
    SALTHYP_INFORMATION: {
      header: "Salt",
      [FeedbackType.STORYDATA]: {
        question:
          "Too much salt in your diet can lead to increased blood pressure. Depending on your genes, reducing salt can be particulary effective at lowering blood pressure. Would you like to know if this is true for you?",
        description:
          "Salt is vital and must be supplied to the body every day through food. However, too much salt in the food can lead to increased blood pressure. The more salt is in the body, the more fluid the body needs. If the salt intake increases, the required water is removed from the cells. In order to flush the salt out of the blood via the kidneys as quickly as possible, the body increases the blood pressure. This effect is particularly strong in certain genetic types. Therefore, it may be useful to reduce the salt intake from one's diet",
        GOOD:
          "Good to know! A reduced salt intake is particularly effective at lowering blood pressure and the prevention of hypertension",
        GOOD_list: [
          "Salt can be consumed in the diet.",
          "Your genes mean that reducing salt is particularly effective.",
          "This means there is a normal influence on blood pressure.",
        ],
        BAD:
          "Due to your genetic variation, a reduced salt intake is generally effective at lowering blood pressure and the prevention of hypertension.",
        BAD_list: [
          "Salt can be consumed in the diet.",
          "Your genes mean that reducing salt is generally effective.",
          "This means there is a strong influence on blood pressure.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Would you like a recommendation based on whether reducing salt is particulary effective at reducing your blood pressure?",
        GOOD:
          "If you suffer from high blood pressure, a salt reduction can have a very positive effect, i.e. lower your blood pressure.",
        BAD: "A salt reduction is good for you, but it only has a moderate effect on lowering your blood pressure.",
      },
    },
    CAFFEINE_INFORMATION: {
      header: "Caffeine",
      [FeedbackType.STORYDATA]: {
        question:
          "Coffee contains beneficial antioxidents, but also caffeine that can affect your health. Your genes determine if you can sufficiently neutralise caffeine. Would you like to know if you can neutralise caffeine?",
        description:
          "Coffee has been one of the world's most popular drinks for centuries. Numerous studies have already shown that moderate coffee consumption can have many beneficial health effects. However, this is also determined by genetics.Coffee contains many different ingredients. For example, it can provide our body with high levels of antioxidants that protect cells from harmful radicals. It also contains unhealthy caffeine, which is broken down in the body by the CYP1A2 gene. A common genetic variation can slow this degradation, which can affect your health.",
        GOOD:
          "Fancy a coffee?! Caffeine is broken down normally in your body and healthy antioxidants in coffee protects against free radicals.",
        GOOD_list: [
          "In a cup of coffee there are both healthy antioxidants and unhealthy caffeine.",
          "Your CYP1A2 gene breaks down caffeine normally.",
          "This means caffeine is neutralised in your body.",
          "The result is healthy antioxidants protect from unhealthy substances.",
        ],
        BAD:
          "Due to your genetic variation, although healthy antioxidants in coffee protects against free radicals, caffeine is only broken down very slowly in your body and can impair your health.",
        BAD_list: [
          "In a cup of coffee there are both healthy antioxidants and unhealthy caffeine.",
          "Your CYP1A2 gene variation means caffeine is broken down slowly.",
          "The result is caffeine is not neutralised and can negatively affect your health",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Would you like a recommendation based on your effectiveness at breaking down caffeine?",
        GOOD:
          "You can break down caffeine normally and therefore consume coffee in moderation. Nevertheless, please pay attention to a healthy lifestyle.",
        BAD:
          "You should better avoid caffeine altogether or at least severely limit its consumption. Try drinking decaffeinated coffee or tea instead.",
      },
    },
    Q10_INFORMATION: {
      header: "Coenzyme Q10",
      [FeedbackType.STORYDATA]: {
        question:
          "Coenzyme Q10 is considered a powerful antioxidant and can protect against free radicals if activated in the body. Would you like to know if Coenzyme Q10 is activated and used in your body?",
        description:
          "Coenzyme Q10 is considered a powerful antioxidant. It is absorbed through one's diet and protects from free radicals. Coenzyme Q10 is an important substance that protects the body from toxic substances, so- called free radicals. But Q10 is not effective in its original form. It must first be converted into its active form inside the body before it can neutralize free radicals. A particular gene (NQO1) is responsible for this transformation.",
        GOOD:
          "Look at you! Inactive Coenzyme Q10 can be sufficiently activated in your body and therefore provides protection from free radicals",
        GOOD_list: [
          "The NQ01 gene produces the NQ01 protein.",
          "This means, Q10 is converted in to its active form ubiquinol.",
          "Ubiquinol helps to neutralise free radicals in the body.",
        ],
        BAD:
          "Due to your genetic variation, inactive Coenzyme Q10 cannot be sufficiently activated and so does not protect from free radicals. As Q10 remains ineffective, other antioxidants can protect from free radicals.",
        BAD_list: [
          "The NQ01 gene does not produce NQO1 proteins.",
          "This means Q10, in your diet, is not being activated.",
          "Other antioxidants can be used to protect from free radicals.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Would you like a recommendation based on the effectiveness of Coenzyme Q10 to fight free radicals in your body?",
        GOOD:
          "Your body can convert Q10 into its active form, ubiquinol. Therefore, it is sufficient to take a non-activated Q10 form.",
        BAD:
          "It is important that  you take the active form of coenzyme Q10: Ubiquinol. Treat yourself to other important antioxidants such as vitamin C and selenium. You can give your body additional support with plenty of fresh fruit and vegetables.",
      },
    },
    SELEN_INFORMATION: {
      header: "Selenium",
      [FeedbackType.STORYDATA]: {
        question:
          "Selenium can help your body neutralise harmful free radicals, but some people may need larger amounts of it to be effective. Would you like to know if a normal level of selenium is sufficient for you?",
        description:
          "The GPX1 gene helps bind and neutralize free radicals. To perform this function, the gene requires a sufficient amount of selenium. However, due to a genetic variation, the ability to bind selenium is worse. As a result, the GPX1 gene provides less protection against free radicals.",
        GOOD:
          "Here’s some good information! Your GPX1 gene works properly and therefore provides good protection against free radicals with a normal amount of selenium.",
        GOOD_list: [
          "Your genes produce GPX1 enzymes correctly.",
          "Selenium is necessary for the production of the GPX1 enzyme.",
          "The GPX1 enzyme binds strongly to free radicals and neutralises many of them.",
          "This means free radicals are being neutralised.",
        ],
        BAD:
          "Due to your genetic variation, the GPX1 gene does not work properly and therefore does not provide good protection against free radicals. As free radicals are not being sufficiently neutralised, a larger amount of selenium is required.",
        BAD_list: [
          "Your genes produce GPX1 enzymes that are slightly varied.",
          "Selenium is necessary for the production of the GPX1 enzyme.",
          "Due to the variation, the GPX1 enzyme has a weaker binding ability and can therefore render fewer free radicals harmless.",
          "This means free radicals are not sufficiently neutralised.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Would you like a recommendation based on whether your GPX1 gene is working effectively or not?",
        GOOD:
          "You should ensure you have an adequate supply of selenium in your diet. Brazil nuts, for example, are rich in selenium. If you have a deficiency, you can of course supplement selenium in addition.",
        BAD:
          "You require an increased amount of selenium in your diet. It's important that you ensure you have a sufficient and regular intake of selenium as a dietary supplement.",
      },
    },
    HEAV_INFORMATION: {
      header: "Heavy metals",
      [FeedbackType.STORYDATA]: {
        question:
          "Heavy metals, such as mercury and lead, are absorbed in the body from the environment and can be harmful if not removed by the body. Would you like to know if you can sufficently remove heavy metals?",
        description:
          "Heavy metals such as mercury, lead and cadmium are an integral part of the environment and are absorbed by every human through their diet or skin on a daily basis. Therefore, it is important for the body to bind these pollutants and render them harmless. This task is performed by various detoxification genes. However, if these genes do not function properly, they can not perform their task sufficiently and the increased exposure to heavy metals can endanger one's health.",
        GOOD: "Music to the ears! Absorbed heavy metals can be sufficiently bound and neutralised in your body.",
        GOOD_list: [
          "Heavy metals, such as lead, cadmium and mercury, are absorbed through diet and skin.",
          "Your detoxification genes perform their task sufficiently.",
          "This means heavy metals are being detected and bound.",
          "The result is heavy metals are being excreted.",
        ],
        BAD:
          "Due to your genetic variation, absorbed heavy metals cannot be sufficiently bound and neutralised in the body, leading to limited heavy metal detoxification.",
        BAD_list: [
          "Heavy metals, such as lead, cadmium and mercury, are absorbed through diet and skin.",
          "Your detoxification genes cannot perform their task sufficiently.",
          "Additional minerals from the diet bind heavy metals.",
          "The result would remove heavy metals and restore protection.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Would you like a recommendation based on how effectively you bound and neutralise heavy metals in your body?",
        GOOD:
          "You are an effective detoxifier! Nevertheless, please make sure not to unnecessarily strain your body with avoidable toxins, such as cigarette smoke and amalgam dental fillings.",
        BAD:
          "You should support your body's detoxification performance by drinking enough water and exercising regularly. In addition, we recommend adding nutrients such as selenium and zinc, which support detoxification.",
      },
    },
    HEART_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 21 gene variances that have been identified as influencing your cardiovascular health? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your cardiac health?",
      },
    },
    BONE_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 4 gene variances that have been identified as influencing your bone health? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your bone health?",
      },
    },
    EYE_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 3 gene variances that have been identified as influencing your eye health? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your eye health?",
      },
    },
    BRAIN_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 3 gene variances that have been identified as influencing your brain health? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your brain health?",
      },
    },
    JOINT_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 2 gene variances that have been identified as influencing your joint health? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your joint health?",
      },
    },
    HOMOCYSTEINE_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 3 gene variances that have been identified as influencing how effectively you can regulate homosysteine levels in your blood? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your blood health?",
      },
    },
    HOMOCYSTEINE_B2_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 1 gene variance that has been identified as influencing whether Vitamin B2 is effective at decomposing homosysteine in the blood? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see a recommendation, calculated based on your gene results, whether to increase or not your level of Vitamin B2 to reduce homocysteine levels?",
      },
    },
    HYPERTENSION_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 3 gene variances that have been identified as influencing blood pressure and the effect reducing salt intake has on it? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your blood pressure?",
      },
    },
    METABOLISM_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 8 gene variances that have been identified as influencing metabolism? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your metabolism?",
      },
    },
    OXIDATIVESTRESS_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 5 gene variances that have been identified as influencing oxidativestress and how your body neutralises free radicals? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your ability to neutralise free radicals?",
      },
    },
    DETOX_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 11 gene variances that have been identified as influencing how your body neutralises heavy metals like mercury and lead? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to neutralise heavy metals in your body?",
      },
    },
    GLUTEN_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 2 gene variances that have been identified as influencing your tolerance to gluten? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your health? Note: If you are gluten intolerant you will be recommended to reduce gluten in your diet.",
      },
      [FeedbackType.GENE_INFO]: {
        heading: "Gluten",
        description_1:
          "Gluten helps foods maintain their shape by acting as a glue that holds it together. It also provides elasticity, and this enables bread to rise, and gives foods containing gluten a chewy structure. However, those with a gluten intolerance should avoid it.",
        description_2:
          "Two genes have been identified that effect how your body responds to gluten in your diet. These two genes, when analysed together, can inform you if you have a low to no risk, a moderate risk, or a high risk and the prerequisite for being gluten intolerant.",
        warning: "An increased genetic risk does not automatically mean that you have an intolerance and vice versa.",
        link: "Review the gene analysed",
      },
      [FeedbackType.GENE_RISK_INFO]: {
        heading: "Gluten Intolerance",
        content_1:
          "Gluten intolerances come in different forms, such as gluten intolerance, a wheat allergy or celiac disease. If you are diagnosed by your doctor with an intolerance, you should consume products containing gluten very rarely or not at all, depending on its severity.",
        content_2:
          "Typical signs to look out for, which may occur after eating cereal products, would be abdominal pain, bloating, nausea, diarrhoea, or constipation. If you think you have an intolerance, consult a doctor!",
        subheading: "What to do if you have an intolerance?",
        subheading_content: `Of course, it's hard to give up pizza, pasta, cornflakes, puffed rice or couscous! However, most supermarkets already have a wide range of substitute products, such as gluten-free bread or pasta, so you don't have to give up much. These products are often made from other types of flour. When reading food labels, look out for the word "GF" (gluten-free) and the letter "A" for allergens. Many foods, particularly those that are processed, contain gluten in additives, such as malt flavouring and modified food starch.`,
        food_sources_header: "Gluten-free alternatives",
      },
    },
    LACTOSE_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "I have analysed 1 gene variance that has been identified as influencing your tolerance to laktose? Would you like to see your gene results?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Would you like to see recommendations, calculated based on your gene results, for foods and nutrients you should increase or decrease to improve your health? Note: If you are laktose intolerant you will be recommended to reduce laktose in your diet.",
      },
      [FeedbackType.GENE_INFO]: {
        heading: "Lactose",
        description_1:
          "Lactose - also called milk sugar - is found in milk and dairy products such as cheese, yoghurt and butter, but also in many convenience foods.",
        description_2:
          "There is a gene that affects whether your body makes enough of the enzyme lactase, which is needed to break down lactose in the intestine. This gene can inform you if you have no increased risk, or a very high risk for being lactose intolerant.",
        warning: "An increased genetic risk does not automatically mean that you have an intolerance and vice versa.",
        link: "Review the gene analysed",
      },
      [FeedbackType.GENE_RISK_INFO]: {
        heading: "Lactose Intolerance",
        content_1:
          "With lactose intolerance, lactose is not broken down in the small intestine and can therefore lead to an undesirable fermentation process in the large intestine.",
        content_2:
          "Typical signs would be abdominal pain, bloating, nausea, or diarrhoea one to two hours after consuming dairy products. If you think you are suffering from an intolerance, consult a doctor to check it out!",
        subheading: "What to do if you have an intolerance?",
        subheading_content: `Lactose can be present not only in classic dairy products, but also in mashed potatoes, ice cream, sauces, sausages, and medicines. Depending on the severity, you should always look carefully at the ingredients of your products and look for the letter "G" when allergens are listed. Most supermarkets already have a wide range of lactose-free substitutes.`,
        food_sources_header: "Lactose-free alternatives",
      },
    },
    TOTAL_INFORMATION: {
      [FeedbackType.NUTRIENTS]: {
        question:
          "In addition to providing a list of nutirents for specific areas, I can also offer you an overall list of nutirents to increase or reduce to help promote health in general. Would you like to view yours now?",
      },
    },
    GENES_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          "Your genes can tell a lot about you and the influence nutrition and lifestyle has on maintaining good health. Would you like to know more about how genes work?",
        description:
          "There are four different nucleobases in human DNA: adenine (A), thymine (T), guanine (G) and cytosine (C). Nucleic bases are the names of the building blocks that make up your DNA. The sequence of these four bases of DNA encodes the genetic information. That is why the four letters A, C, G, and T are also known as the alphabet of life. A genotype refers to the pair of SNPs that were inherited at a specific chromosomal position, one on the maternal and one on the paternal side.",
      },
    },
    GENESTORY_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          "Your genes are analysed and I will present some results as gene stories. Would you like to know more about gene stories?",
        description:
          "First of all, different genetic variations are examined, all of which have an impact on different areas of your health. For example, genes from the heart category have an impact on heart health and the associated risks (increased cholesterol, increased triglyceride levels, etc.). Depending on these individual genetic characteristics, the associated evaluation lists the micronutrients that have a positive or negative influence on this area of health and evaluated on the basis of your individual genetic strengths and weaknesses. If the risk is high, micronutrients that have been shown to have a preventive effect are increased and substances that further increase the risk are reduced.",
      },
    },
    RECOMMENDATION_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          "Based on your personal genetic profile, I will provide you with a recommendation that can be used to improve your health. Would you like to learn more about how we make recommendations?",
        description:
          "Working together with our nutritionalist, we review each genetic profile and offer a personal recommendations that you can put into action today to help you optimise your health, based on your unique genetic traits.",
      },
    },
    NUTRIENTS_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          "I can provide you with a list of recommended nutrients to increase or reduce to help promote your health in specific areas of your body. Would you like to know how we display the results?",
        description:
          "Micronutrients (vitamins, minerals and trace elements) are essential and vital for many of our body functions. Some of these substances can be produced by the body itself, others have to be ingested through food. Since deficiencies can lead to serious health problems, it is important to supply these substances in sufficient quantities. However, since not everyone has the same genetic requirements, the need for micronutrients is also different. An analysis of genetic strengths and weaknesses can therefore help to supply the right micronutrients in the right amount and to avoid particularly negative substances. Everyone has different genetic risks, for example, which can be minimized by eating the right diet or increasing and reducing various micronutrients. There are also substances that, due to genetic variations, cannot be properly converted and activated. In this case, too, this knowledge helps to select the right micronutrients and thus foods.",
      },
    },
    INTOLERANCE_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          "I can help you know if you can process milk and also the gluten found in cereals. Would you like to know more about this?",
        description:
          "A DNA analysis cannot determine any incompatibilities. Rather, it is about determining genetic weaknesses (such as predisposition to bad blood values, etc.) and taking preventive action against them with an optimized diet. If there are intolerances, these foods should be avoided regardless of the DNA result.",
      },
    },
    NUTRIENT_OVERVIEW_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          "In addition to providing a list of nutirents for specific areas, I can also offer you an overall list of nutirents to increase or reduce to help promote health in general. Would you like to view yours now?",
        description: "LINK TO NUTRIENT OVERIEW",
      },
    },
    RECOMMENDED_DAILY_MICRO: {
      [FeedbackType.MICRONUTRIENTS]: {
        question:
          "Most people have heard about Recomended Daily Allowances (RDA) for certain nutrients, however, because of your unique genes I can provide you with a personalised RDA. Would you like to view yours now?",
        description: "LINK TO PERSONALISED DAILY ALLOWANCES",
      },
    },
  },
  shop_button: {
    heading: "Personalise your twin!",
    content:
      "Order <0>your DNA test kit</0> from our online shop today and personalise your digital twin using your very own genetic profile.",
    button: "Shop now",
  },
  user_data: {
    header: "Your data",
    info: {
      account_info: {
        header: "Your account",
        email: "E-mail address",
        change_password_button: "Change Password",
      },
      digital_twin_info: {
        header: "Your Digital Twin",
        firstname: {
          label: "First Name",
        },
        lastname: {
          label: "Last Name",
        },
        gender: {
          label: "Gender",
        },
        weight: {
          label: "Weight (kg)",
        },
        date_of_birth: {
          label: "Date of Birth",
        },
        height: {
          label: "Height (cm)",
        },
        ethnicity: {
          label: "Ethnicity",
        },
        diet_preference: {
          label: "Diet Preference",
        },
      },
      save_button: "Save changes",
    },
    change_password: {
      header: "Update password",
      subtitle:
        "We recommend that you create a strong password with more than 8 characters, including upper- and lower-case letters, numbers, and special characters to help protect your data.", // TODO: Translate
      old_password: "Old Password",
      new_password: "New password",
      confirm_new_password: "Repeat new password",
      button: "Save new password",
      messages: {
        password_updated: "Password has been updated.",
        invalid_old_password: "Old password is invalid.",
      },
    },
    messages: {
      information_updated: "Changes have been successfully saved.",
    },
  },
  favourites: {
    header: "Your favourites",
    subheader: "Here you can find your bookmarked pages, to help you quickly navigate to your preferred content.",
    info: {
      [MenuItemSubcategory.OVERVIEW]: { 1: "Welcome to myDigitalTwin!" },
      [MenuItemSubcategory.MICRONUTRIENTS_TO_INCREASE]: { 1: "Micronutrients+", 2: "Micronutrients-" },
      [MenuItemSubcategory.FINDINGS]: { 1: "Info" },
      [MenuItemSubcategory.GENEVARIATIONS]: { 1: "Gene Variations", 2: "Your Gene Variations" },
      [MenuItemSubcategory.HEART]: {
        1: "Micronutrients +",
        2: "Micronutrients -",
        3: "Triglycerides",
        4: "Cholesterol",
        5: "OMEGA-3",
      },
      [MenuItemSubcategory.BRAIN]: {
        1: "Micronutrients +",
        2: "Micronutrients -",
        3: "Free radicals",
      },
      [MenuItemSubcategory.BONE]: {
        1: "Micronutrients +",
        2: "Micronutrients -",
        3: "Calcium",
        4: "Vitamin D",
      },
      [MenuItemSubcategory.JOINT]: {
        1: "Micronutrients +",
        2: "Micronutrients -",
      },
      [MenuItemSubcategory.EYE]: {
        1: "Micronutrients +",
        2: "Micronutrients -",
      },
      [MenuItemSubcategory.OXIDATIVESTRESS]: {
        1: "Micronutrients +",
        2: "Micronutrients -",
        3: "Free radicals",
        4: "Coenzyme Q10",
        5: "Selenium",
      },
      [MenuItemSubcategory.METABOLISM]: {
        1: "Micronutrients +",
        2: "Micronutrients -",
      },
      [MenuItemSubcategory.DETOX]: {
        1: "Micronutrients +",
        2: "Micronutrients -",
        3: "Heavy metals",
        4: "Caffeine",
      },
      [MenuItemSubcategory.GLUTEN]: {
        1: "Gluten",
        2: "Gluten Intolerance",
        3: "Micronutrients +",
        4: "Micronutrients -",
      },
      [MenuItemSubcategory.LACTOSE]: {
        1: "Lactose",
        2: "Lactose Intolerance",
        3: "Micronutrients +",
        4: "Micronutrients -",
      },
      [MenuItemSubcategory.HOMOCYSTEINE]: {
        1: "Micronutrients +",
        2: "Micronutrients -",
        3: "Homocysteine",
        4: "Vitamin B2",
      },
      [MenuItemSubcategory.HYPERTENSION]: {
        1: "Micronutrients +",
        2: "Micronutrients -",
        3: "Salt",
      },
      [MenuItemSubcategory.TOTAL]: { 1: "Info" },
      [MenuItemSubcategory.MICRONUTRIENTS]: { 1: "Your Recommended Daily Allowance" },
      [MenuItemSubcategory.RDA]: { 1: "Your Recommended Daily Allowance" },
      [MenuItemSubcategory.FRUCTOSE]: { 1: "Info" },
      [MenuItemSubcategory.USER_DATA]: { 1: "Info" },
      [MenuItemSubcategory.FAVOURITES]: { 1: "Info" },
      [MenuItemSubcategory.RESILIENCE]: { 1: "Resilience" },
    },
  },
  user_reports: {
    header: "Your reports",
    subheader: "The following gene analysis has been processed for you:",
    gene_analysis: {
      title: "Gene analysis",
      result: "Nutrition",
    },
    // TODO: Translate section
    blood_analysis: {
      title: "Blutanalysen",
      button: "Blutanalyse-Kit bestellen",
    },
    tip: {
      title: "Messe regelmäßig deinen Fortschritt",
      content:
        "Wir empfehlen, alle zwei Monate ein Blutanalyse-Kit zu verwenden, um zu überprüfen, wie sich deine Werte verändern.",
    },
  },
  nutrient_modal: {
    // Todo: translate
    food_sources: "Food sources:",
    food_source_sub_header: "Reihenfolge nach Konzentration (1 = höchste)",
    references: "References",
  },
  snackbar: {
    get_nutrient_info_error: "Could not fetch data from backend",
  },
  mental_health: {
    resilience: {
      warning:
        "A genetic predisposition does not mean you cannot adopt techniques to overcome or enhance your resilience.",
      stress: {
        title: "Performance in stressful situations",
        descriptions: {
          [ResilienceLevel.HIGH]:
            "Like 41% of the population, you are more likely to perform better in stressful situations and have greater emotional resilience",
          [ResilienceLevel.NORMAL]: "",
          [ResilienceLevel.LOW]: "",
          [ResilienceLevel.NONE]: "",
        },
      },
      emotion: {
        title: "Emotional resilience",
        descriptions: {
          [ResilienceLevel.HIGH]: "",
          [ResilienceLevel.NORMAL]:
            "Like 44% of the population, you have a normal level of resilience when dealing with stressful situations and negative feelings.",
          [ResilienceLevel.LOW]: "",
          [ResilienceLevel.NONE]: "",
        },
      },
      impulsiveness: {
        title: "Impulsive behaviour",
        descriptions: {
          [ResilienceLevel.HIGH]: "",
          [ResilienceLevel.NORMAL]: "",
          [ResilienceLevel.LOW]: "Like 15% of the population, you are more likely to act impulsively.",
          [ResilienceLevel.NONE]: "",
        },
      },
    },
  },
};
